import { FileText } from "lucide-react";
import { FC } from "react";
import { FormattedMessage } from "react-intl";
import { useGetOrganizationSettings } from "~/api/organizations/organizations";
import logoImage from "~/assets/logo.svg";
import { UnifiedMarkdown } from "~/components/UnifiedMarkdown";
import { Button } from "~/components/ui/button";
import type { SectionView } from "~/features/cases/hooks/useFtueTemplateSectionsView";
import {
  FONT_FAMILY_STACKS,
  isFontFamilyOptionKey,
} from "~/features/settings/constants";
import { useOrganizationParams } from "~/helpers";
import { cn } from "~/lib/utils";

/**
 * Skeleton bar widths (% of container) used as placeholder body lines for
 * sections that haven't finished generating yet. The mix of widths visually
 * approximates a paragraph without making a promise about real length.
 */
const PLACEHOLDER_LINE_WIDTHS = [98, 92, 96, 84, 90, 70];

interface FtueReportPreviewProps {
  /**
   * Ordered list of report sections, each either completed (with content) or
   * still in flight (rendered as a placeholder block). Sections are rendered
   * in array order; the caller is responsible for sorting by `order`.
   */
  sections: SectionView[];
  onContinue: () => void;
}

export const FtueReportPreview: FC<FtueReportPreviewProps> = ({
  sections,
  onContinue,
}) => {
  const { organizationId } = useOrganizationParams();
  const { data: settings } = useGetOrganizationSettings(organizationId);

  const previewLogo = settings?.logoUrl ?? logoImage;
  const fontFamily = settings?.fontFamily ?? "serif";
  const lineSpacing = settings?.lineSpacing ?? "1.5";

  const fontFamilyStyle = isFontFamilyOptionKey(fontFamily)
    ? FONT_FAMILY_STACKS[fontFamily]
    : fontFamily;

  // "Ready" matches what the user actually sees: every section both reports a
  // completed status AND has its content loaded. A section that is `completed`
  // but whose body is still being fetched still renders as a placeholder, so
  // we treat the report as still generating in that case.
  const isReportReady =
    sections.length > 0 &&
    sections.every(
      (s) => s.status === "completed" && (s.content?.trim().length ?? 0) > 0
    );

  return (
    <div className="flex flex-col lg:flex-row min-h-[calc(100vh-200px)] px-4 gap-6 max-w-7xl mx-auto w-full">
      {/* Left Panel — Info */}
      <div className="lg:w-1/3 flex flex-col justify-center space-y-6 py-8">
        <div className="space-y-3">
          <h1 className="text-3xl font-semibold text-foreground">
            {isReportReady ? (
              <FormattedMessage
                id="ftue.reportPreview.title"
                defaultMessage="Your Template Is Ready!"
              />
            ) : (
              <FormattedMessage
                id="ftue.reportPreview.generating.title"
                defaultMessage="Generating Your Sample Report…"
              />
            )}
          </h1>
          <p className="text-lg text-muted-foreground">
            {isReportReady ? (
              <FormattedMessage
                id="ftue.reportPreview.subtitle"
                defaultMessage="Here's a taste of what you'll get for each new case — ready to review and send to your clients."
              />
            ) : (
              <FormattedMessage
                id="ftue.reportPreview.generating.subtitle"
                defaultMessage="Filling in your template section by section. The preview will update as each one finishes."
              />
            )}
          </p>
        </div>

        <p className="text-muted-foreground">
          <FormattedMessage
            id="ftue.reportPreview.uploadCta"
            defaultMessage="Upload your case documents to get a full report — formatted, cited, and ready to deliver to your clients."
          />
        </p>

        <Button size="lg" onClick={onContinue} className="mt-4">
          <FormattedMessage
            id="ftue.reportPreview.continue"
            defaultMessage="Continue"
          />
        </Button>
      </div>

      {/* Right Panel — PDF-like Report Viewer */}
      <div className="lg:w-2/3 flex flex-col py-8">
        <div className="flex items-center gap-2 mb-4 text-muted-foreground">
          <FileText className="w-4 h-4" />
          <span className="text-sm font-medium">
            <FormattedMessage
              id="ftue.reportPreview.previewLabel"
              defaultMessage="Report Preview"
            />
          </span>
        </div>
        <div
          className="flex-1 flex flex-col rounded-lg border bg-white dark:bg-card shadow-sm overflow-hidden"
          style={{
            maxHeight: "calc(100vh - 300px)",
            fontFamily: fontFamilyStyle,
            lineHeight: lineSpacing,
          }}
        >
          {/* Document Header — Case info + Logo */}
          <div className="flex items-start justify-between px-8 pt-8 pb-4 border-b flex-shrink-0">
            <div className="text-xs text-gray-600 dark:text-muted-foreground">
              <FormattedMessage
                id="settings.preview.caseName"
                defaultMessage="Case Name"
              />
              :{" "}
              <FormattedMessage
                id="settings.preview.exampleCase"
                defaultMessage="Example case"
              />
              <br />
              <FormattedMessage
                id="settings.preview.dateOfBirth"
                defaultMessage="Date of Birth"
              />
              : 3/4/1958
              <br />
              <FormattedMessage
                id="settings.preview.claimNumber"
                defaultMessage="Claim/Case #"
              />
              : R2515575
            </div>
            <img src={previewLogo} alt="Logo" className="h-8 object-contain" />
          </div>

          {/* Document Body — Section-by-section */}
          <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
            {sections.map((section, index) => (
              <SectionBlock
                key={section.id}
                section={section}
                isFirst={index === 0}
              />
            ))}
          </div>

          {/* Document Footer */}
          <div className="flex items-center justify-between px-8 py-3 border-t text-xs text-gray-400 dark:text-muted-foreground flex-shrink-0">
            <span>
              <FormattedMessage
                id="settings.preview.poweredBy"
                defaultMessage="Powered by Sky AI"
              />
            </span>
            <span>
              <FormattedMessage
                id="settings.preview.pageCount"
                defaultMessage="1 of 1"
              />
            </span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-3 text-center">
          <FormattedMessage
            id="ftue.reportPreview.stylingNote"
            defaultMessage="You can add your branding and customize styling later."
          />
        </p>
      </div>
    </div>
  );
};

interface SectionBlockProps {
  section: SectionView;
  isFirst: boolean;
}

const SectionBlock: FC<SectionBlockProps> = ({ section, isFirst }) => {
  const body = section.content?.trim() ?? "";
  const isCompleted = section.status === "completed" && body.length > 0;

  if (isCompleted) {
    return (
      <UnifiedMarkdown
        className="prose prose-sm dark:prose-invert max-w-none"
        withHeadings
        components={
          isFirst
            ? {
                h1: ({ node: _node, ...props }) => (
                  <h1
                    {...props}
                    className="text-2xl font-bold mb-3 normal-case"
                  />
                ),
              }
            : undefined
        }
      >
        {buildSectionMarkdown(section.title, body, isFirst)}
      </UnifiedMarkdown>
    );
  }

  return <SectionPlaceholder title={section.title} isFirst={isFirst} />;
};

/**
 * Build markdown for one completed section: a single H1 (first section) or
 * H2 (subsequent) heading followed by the section body. Single newlines in
 * the body are promoted to paragraph breaks to match how the backend tends
 * to emit content.
 */
function buildSectionMarkdown(
  title: string,
  body: string,
  isFirst: boolean
): string {
  const hash = isFirst ? "#" : "##";
  const trimmedTitle = title.trim();
  const normalizedBody = body.replace(/(?<!\n)\n(?!\n)/g, "\n\n");
  if (!trimmedTitle) return normalizedBody;
  return `${hash} ${trimmedTitle}\n\n${normalizedBody}`;
}

interface SectionPlaceholderProps {
  title: string;
  isFirst: boolean;
}

const SectionPlaceholder: FC<SectionPlaceholderProps> = ({
  title,
  isFirst,
}) => {
  const HeadingTag = isFirst ? "h1" : "h2";
  return (
    <div
      data-testid="ftue-report-preview-section-placeholder"
      aria-live="polite"
      aria-busy="true"
    >
      <HeadingTag
        className={cn(
          isFirst
            ? "text-2xl font-bold normal-case"
            : "text-xl font-semibold normal-case",
          "mb-3 text-muted-foreground/60"
        )}
      >
        {title}
      </HeadingTag>
      <div className="space-y-2.5" aria-hidden="true">
        {PLACEHOLDER_LINE_WIDTHS.map((w, i) => (
          <div
            key={i}
            className="h-3 rounded bg-muted-foreground/15"
            style={{ width: `${w}%` }}
          />
        ))}
      </div>
    </div>
  );
};
